import type { CombinedTabKey, DiagnosticTabKey, DimensionType, UsageQueryFnName, UsageTabKey } from '../../types';
import { AggregationType, Granularity, LimitNames, MeterType, UsageTab } from '../../types';

import {
  aiRunsDimensions,
  clientSideMAUDimensions,
  contextsDimensions,
  dataExportCombinedDimensions,
  dataExportDimensions,
  experimentationEventsDimensions,
  experimentationKeysDimensions,
  experimentationMAUDimensions,
  observabilityDimensions,
  peakConcurrentConnectionsDimensions,
  serviceConnectionsDimensions,
  warehouseExportDimensions,
} from './metricDimensions';

/**
 * Opt-in config for the dismissible "Set applicationId in your SDK..." info banner shown on the
 * detail page when the SDK App ID dimension is selected. Tabs without this config don't render
 * the banner.
 */
export type SdkAppIdAlertConfig = {
  /**
   * SDK qualifier inserted into the sentence, e.g. "your server-side SDKs".
   * Defaults to "your SDKs" when omitted.
   */
  sdkDescription?: string;
  /** Optional trailing note rendered on its own line. Omit for no note. */
  extraNote?: string;
};

type BaseTabConfig = {
  queryFnName: UsageQueryFnName; // Rest API query function name
  meterType: MeterType; // Meter type (sum, unique, etc.)
  dimensions: DimensionType[]; // Supported group by dimensions
  aggregationTypes: AggregationType[]; // Supported aggregation types
  /**
   * Default aggregation type for this tab.
   * - Detail pages (Plan and Diagnostic): used as the initial aggregation type.
   * - Diagnostic overview: used as the chart aggregation type.
   * - Plan Usage overview: ignored — always uses MONTH_TO_DATE to align numbers with billing.
   */
  defaultAggregationType?: AggregationType;
  totalAggregationTypeOverride?: AggregationType; // If set, overrides the aggregation type used to compute totals
  granularities: Granularity[]; // Supported granularities
  precision?: number; // Number of significant digits to display in the chart
  sdkQueryFnName?: UsageQueryFnName; // V1 usage APIs have a separate query function for SDK versions
  sdkAppIdAlert?: SdkAppIdAlertConfig; // Opt-in SDK App ID info banner shown on detail pages
  hasProjectionData?: boolean; // Tab whose backend response may include projectedSeries
};

type PlanTabConfig = BaseTabConfig & {
  visibility: TabVisibility.PLAN_ONLY | TabVisibility.PLAN_OR_DIAGNOSTIC;
  limitName: LimitNames;
};

type DiagnosticTabConfig = BaseTabConfig & {
  visibility: TabVisibility.DIAGNOSTIC_ONLY;
  limitName?: never;
};

type HiddenTabConfig = BaseTabConfig & {
  visibility: TabVisibility.HIDDEN;
  limitName?: LimitNames;
};

type CombinedTabConfig = Omit<BaseTabConfig, 'queryFnName' | 'sdkQueryFnName'> & {
  queryFnName?: never;
  sdkQueryFnName?: never;
  visibility: TabVisibility.HIDDEN;
  limitName: LimitNames;
};

type UsageTabConfig = {
  [K in Exclude<UsageTabKey, DiagnosticTabKey | CombinedTabKey>]: PlanTabConfig | HiddenTabConfig;
} & {
  [K in DiagnosticTabKey]: DiagnosticTabConfig | HiddenTabConfig;
} & {
  [K in CombinedTabKey]: CombinedTabConfig;
};

export enum TabVisibility {
  /**
   * Usage tab is only visible in Plan Usage if the metric is provisioned on the subscription.
   */
  PLAN_ONLY = 'PLAN_ONLY',
  /**
   * Usage tab is visible in Plan Usage if the metric is provisioned on the subscription,
   * otherwise always show in Diagnostic Usage.
   */
  PLAN_OR_DIAGNOSTIC = 'PLAN_OR_DIAGNOSTIC',
  /**
   * Usage tab is only visible in Diagnostic Usage.
   */
  DIAGNOSTIC_ONLY = 'DIAGNOSTIC_ONLY',
  /**
   * Use for new usage metrics that aren't released publicly yet. Can be overridden via flag
   * targetting for testing.
   */
  HIDDEN = 'HIDDEN',
}

/**
 * Ordered config for tabs shown on Plan and Diagnostic Usage pages.
 *
 * Plan Usage:
 * - Overview always uses MONTH_TO_DATE (needs to match billing).
 * - Detail pages default to MONTH_TO_DATE but respect `defaultAggregationType` if set.
 *
 * Diagnostic Usage:
 * - Overview and detail pages default to INCREMENTAL but respect `defaultAggregationType` if set.
 * - Use `totalAggregationTypeOverride` when the chart type can't be summed into a total.
 */
export const usageTabConfig: UsageTabConfig = {
  [UsageTab.SERVICE_CONNECTIONS]: {
    queryFnName: 'getServiceConnectionsUsageQuery',
    limitName: LimitNames.HOSTS,
    meterType: MeterType.SUM,
    dimensions: serviceConnectionsDimensions,
    aggregationTypes: [AggregationType.MONTH_TO_DATE, AggregationType.INCREMENTAL],
    granularities: [Granularity.HOURLY, Granularity.DAILY, Granularity.MONTHLY],
    visibility: TabVisibility.PLAN_OR_DIAGNOSTIC,
    precision: 3,
    hasProjectionData: true,
    sdkAppIdAlert: {
      sdkDescription: 'your server-side SDK\u2019s',
      extraNote: 'Relay Proxy support requires Relay Proxy v8.17.8 or later.',
    },
  },
  [UsageTab.CLIENT_SIDE_MAU]: {
    queryFnName: 'getMAUClientsideUsageQuery',
    limitName: LimitNames.MONTHLY_ACTIVE_USERS,
    meterType: MeterType.UNIQUE,
    dimensions: clientSideMAUDimensions,
    aggregationTypes: [AggregationType.MONTH_TO_DATE, AggregationType.INCREMENTAL, AggregationType.ROLLING_30DAY],
    granularities: [Granularity.DAILY, Granularity.MONTHLY],
    visibility: TabVisibility.PLAN_OR_DIAGNOSTIC,
    sdkAppIdAlert: {
      sdkDescription: 'your client-side SDK\u2019s',
    },
  },
  [UsageTab.AI_RUNS]: {
    queryFnName: 'getAIRunsUsageQuery',
    limitName: LimitNames.AI_RUNS,
    meterType: MeterType.SUM,
    dimensions: aiRunsDimensions,
    aggregationTypes: [AggregationType.MONTH_TO_DATE, AggregationType.INCREMENTAL],
    granularities: [Granularity.HOURLY, Granularity.DAILY, Granularity.MONTHLY],
    visibility: TabVisibility.PLAN_ONLY,
    sdkAppIdAlert: {},
  },
  [UsageTab.EXPERIMENTATION_MAU]: {
    queryFnName: 'getMAUTotalUsageQuery',
    limitName: LimitNames.EXPERIMENTATION_MAU,
    meterType: MeterType.UNIQUE,
    dimensions: experimentationMAUDimensions,
    aggregationTypes: [AggregationType.MONTH_TO_DATE, AggregationType.INCREMENTAL, AggregationType.ROLLING_30DAY],
    granularities: [Granularity.DAILY, Granularity.MONTHLY],
    visibility: TabVisibility.PLAN_ONLY,
    sdkAppIdAlert: {},
  },
  [UsageTab.EXPERIMENTATION_KEYS]: {
    queryFnName: 'getExperimentationKeysUsageQuery',
    limitName: LimitNames.EXPERIMENTATION_KEYS,
    meterType: MeterType.UNIQUE,
    dimensions: experimentationKeysDimensions,
    aggregationTypes: [AggregationType.MONTH_TO_DATE, AggregationType.INCREMENTAL],
    granularities: [Granularity.HOURLY, Granularity.DAILY, Granularity.MONTHLY],
    visibility: TabVisibility.PLAN_ONLY,
  },
  [UsageTab.EXPERIMENTATION_EVENTS]: {
    queryFnName: 'getExperimentationEventsUsageQuery',
    limitName: LimitNames.EVENTS_RECEIVED,
    meterType: MeterType.SUM,
    dimensions: experimentationEventsDimensions,
    aggregationTypes: [AggregationType.MONTH_TO_DATE, AggregationType.INCREMENTAL],
    granularities: [Granularity.HOURLY, Granularity.DAILY, Granularity.MONTHLY],
    visibility: TabVisibility.PLAN_ONLY,
  },
  [UsageTab.DATA_EXPORT]: {
    queryFnName: 'getDataExportEventsUsageQuery',
    limitName: LimitNames.EVENTS_PUBLISHED,
    meterType: MeterType.SUM,
    dimensions: dataExportDimensions,
    aggregationTypes: [AggregationType.MONTH_TO_DATE, AggregationType.INCREMENTAL],
    granularities: [Granularity.HOURLY, Granularity.DAILY, Granularity.MONTHLY],
    visibility: TabVisibility.PLAN_ONLY,
  },
  [UsageTab.DATA_EXPORT_COMBINED]: {
    limitName: LimitNames.EVENTS_PUBLISHED,
    meterType: MeterType.SUM,
    dimensions: dataExportCombinedDimensions,
    aggregationTypes: [AggregationType.MONTH_TO_DATE, AggregationType.INCREMENTAL],
    granularities: [Granularity.HOURLY, Granularity.DAILY, Granularity.MONTHLY],
    visibility: TabVisibility.HIDDEN,
  },
  [UsageTab.TOTAL_CONTEXTS]: {
    queryFnName: 'getContextsTotalUsageQuery',
    meterType: MeterType.UNIQUE,
    dimensions: contextsDimensions,
    aggregationTypes: [AggregationType.MONTH_TO_DATE, AggregationType.INCREMENTAL, AggregationType.ROLLING_30DAY],
    granularities: [Granularity.DAILY, Granularity.MONTHLY],
    visibility: TabVisibility.DIAGNOSTIC_ONLY,
    sdkAppIdAlert: {},
  },
  [UsageTab.CLIENT_SIDE_CONTEXTS]: {
    queryFnName: 'getContextsClientsideUsageQuery',
    meterType: MeterType.UNIQUE,
    dimensions: contextsDimensions,
    aggregationTypes: [AggregationType.MONTH_TO_DATE, AggregationType.INCREMENTAL, AggregationType.ROLLING_30DAY],
    granularities: [Granularity.DAILY, Granularity.MONTHLY],
    visibility: TabVisibility.DIAGNOSTIC_ONLY,
    sdkAppIdAlert: {
      sdkDescription: 'your client-side SDK\u2019s',
    },
  },
  [UsageTab.SERVER_SIDE_CONTEXTS]: {
    queryFnName: 'getContextsServersideUsageQuery',
    meterType: MeterType.UNIQUE,
    dimensions: contextsDimensions,
    aggregationTypes: [AggregationType.MONTH_TO_DATE, AggregationType.INCREMENTAL, AggregationType.ROLLING_30DAY],
    granularities: [Granularity.DAILY, Granularity.MONTHLY],
    visibility: TabVisibility.DIAGNOSTIC_ONLY,
    sdkAppIdAlert: {
      sdkDescription: 'your server-side SDK\u2019s',
    },
  },
  [UsageTab.CLIENT_CONNECTIONS]: {
    queryFnName: 'getClientConnectionsUsageQuery',
    meterType: MeterType.UNIQUE,
    dimensions: [],
    aggregationTypes: [AggregationType.INCREMENTAL],
    granularities: [Granularity.DAILY],
    visibility: TabVisibility.DIAGNOSTIC_ONLY,
  },
  [UsageTab.PEAK_CONCURRENT_CONNECTIONS]: {
    queryFnName: 'getPeakConcurrentConnectionsUsageQuery',
    sdkQueryFnName: 'getPeakConcurrentConnectionsUsageBySdkVersionQuery',
    meterType: MeterType.UNIQUE,
    dimensions: peakConcurrentConnectionsDimensions,
    aggregationTypes: [AggregationType.INCREMENTAL],
    granularities: [Granularity.DAILY],
    visibility: TabVisibility.DIAGNOSTIC_ONLY,
  },
  // Warehouse data export is a flat-fee, niche add-on metric (no per-row
  // limit/entitlement), so it has no `limitName` and lives in Diagnostic Usage. Ordered after the
  // universal contexts/connections diagnostics so it renders last (in the overflow menu).
  [UsageTab.WAREHOUSE_DATA_EXPORT]: {
    queryFnName: 'getWarehouseExportUsageQuery',
    meterType: MeterType.SUM,
    dimensions: warehouseExportDimensions,
    aggregationTypes: [AggregationType.MONTH_TO_DATE, AggregationType.INCREMENTAL],
    granularities: [Granularity.HOURLY, Granularity.DAILY, Granularity.MONTHLY],
    visibility: TabVisibility.DIAGNOSTIC_ONLY,
  },
  [UsageTab.SESSIONS]: {
    queryFnName: 'getObservabilitySessionsUsageQuery',
    limitName: LimitNames.SESSIONS,
    meterType: MeterType.SUM,
    dimensions: observabilityDimensions,
    aggregationTypes: [AggregationType.MONTH_TO_DATE, AggregationType.INCREMENTAL],
    granularities: [Granularity.DAILY, Granularity.MONTHLY],
    visibility: TabVisibility.PLAN_OR_DIAGNOSTIC,
  },
  [UsageTab.ERRORS]: {
    queryFnName: 'getObservabilityErrorsUsageQuery',
    limitName: LimitNames.ERRORS,
    meterType: MeterType.SUM,
    dimensions: observabilityDimensions,
    aggregationTypes: [AggregationType.MONTH_TO_DATE, AggregationType.INCREMENTAL],
    granularities: [Granularity.DAILY, Granularity.MONTHLY],
    visibility: TabVisibility.PLAN_OR_DIAGNOSTIC,
  },
  [UsageTab.LOGS]: {
    queryFnName: 'getObservabilityLogsUsageQuery',
    limitName: LimitNames.LOGS,
    meterType: MeterType.SUM,
    dimensions: observabilityDimensions,
    aggregationTypes: [AggregationType.MONTH_TO_DATE, AggregationType.INCREMENTAL],
    granularities: [Granularity.DAILY, Granularity.MONTHLY],
    visibility: TabVisibility.PLAN_OR_DIAGNOSTIC,
  },
  [UsageTab.TRACES]: {
    queryFnName: 'getObservabilityTracesUsageQuery',
    limitName: LimitNames.TRACES,
    meterType: MeterType.SUM,
    dimensions: observabilityDimensions,
    aggregationTypes: [AggregationType.MONTH_TO_DATE, AggregationType.INCREMENTAL],
    granularities: [Granularity.DAILY, Granularity.MONTHLY],
    visibility: TabVisibility.PLAN_OR_DIAGNOSTIC,
  },
  [UsageTab.METRICS]: {
    queryFnName: 'getObservabilityMetricsUsageQuery',
    meterType: MeterType.AVERAGE,
    dimensions: observabilityDimensions,
    aggregationTypes: [AggregationType.AVERAGE, AggregationType.MONTH_TO_DATE],
    defaultAggregationType: AggregationType.AVERAGE,
    totalAggregationTypeOverride: AggregationType.MONTH_TO_DATE,
    granularities: [Granularity.HOURLY, Granularity.DAILY, Granularity.MONTHLY],
    visibility: TabVisibility.DIAGNOSTIC_ONLY,
  },
  [UsageTab.VEGA_AI]: {
    queryFnName: 'getVegaAIUsageQuery',
    meterType: MeterType.SUM,
    dimensions: observabilityDimensions,
    aggregationTypes: [AggregationType.MONTH_TO_DATE, AggregationType.INCREMENTAL],
    granularities: [Granularity.DAILY, Granularity.MONTHLY],
    visibility: TabVisibility.HIDDEN,
    limitName: LimitNames.VEGA_AI,
  },
} as const;
